import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import Loader from '../../atoms/Loader/Loader';
import { SizeVariants } from '../../../theme/themes';
import Select from '../../atoms/Select/Select';
import { Option } from '../../atoms/Select/Option';
import { Typography } from '../../atoms/Typography';

const useCSS = makeCSS(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column'
  },
  select: {
    position: 'absolute',
    top: 16,
    right: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  },
  iframe: {
    border: 'none'
  }
}));

interface Html5BannerProps {
  src: string;
  title: string;
  dimension: Dimension;
}

type Dimension = {
  width: number;
  height: number;
};

const BANNER_DIMENSIONS = [
  { width: 320, height: 50 },
  { width: 320, height: 100 },
  { width: 300, height: 50 },
  { width: 300, height: 250 },
  { width: 300, height: 600 },
  { width: 728, height: 90 },
  { width: 468, height: 60 },
  { width: 160, height: 600 },
  { width: 970, height: 250 },
  { width: 970, height: 90 }
];
const getDimension = (dimension: Dimension) =>
  dimension.width + 'x' + dimension.height;

export const Html5Banner = (props: Html5BannerProps) => {
  const { src, title, dimension: initialSize } = props;
  const [iframeReady, setIframeReady] = useState(false);
  const [dimension, setDimension] = useState<Dimension>(
    initialSize ?? BANNER_DIMENSIONS[3]
  );

  useEffect(() => {
    if (initialSize) {
      const index = BANNER_DIMENSIONS.findIndex(
        (d) => d.width === initialSize.width && d.height === initialSize.height
      );
      if (index > -1) {
        setDimension(BANNER_DIMENSIONS[index]);
      }
    }
  }, [initialSize]);

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleLoad = useCallback(() => {
    setIframeReady(true);
  }, []);

  const classes = useCSS();
  return (
    <div className={classes.root} ref={containerRef}>
      <div className={classes.select}>
        <Typography>Dimension:</Typography>
        <Select value={getDimension(dimension)}>
          {BANNER_DIMENSIONS.map((dim) => {
            const value = getDimension(dim);
            return (
              <Option
                value={value}
                key={value}
                onClick={() => setDimension(dim)}
              >
                {value}
              </Option>
            );
          })}
        </Select>
      </div>
      {!iframeReady && (
        <span className={classes.loader}>
          <Loader size={SizeVariants['4xl']} />
        </span>
      )}

      <iframe
        ref={iframeRef}
        src={src}
        sandbox={'allow-scripts allow-same-origin'}
        referrerPolicy="no-referrer"
        title={title || 'HTML5 banner'}
        allowFullScreen={true}
        className={classes.iframe}
        loading="lazy"
        onLoad={handleLoad}
        style={{ ...dimension, backgroundColor: '#000' }}
      ></iframe>
    </div>
  );
};
